import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createLeagueApi } from '../utils/api';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30; // matches LeagueLayout.jsx's existing ready-poll ceiling (30 * 2s = 60s)

/**
 * Fetches a league's season history and exposes the discover/backfill actions
 * the season switcher needs. One instance per currently-expanded league.
 *
 * @param {string} leagueId
 * @param {Function} authFetch - from useClerkApi()
 * @param {string} [apiBaseUrl] - a share link's base; undefined uses the API's own
 */
export function useSeasonSwitcher(leagueId, authFetch, apiBaseUrl) {
  const [seasons, setSeasons] = useState(null);
  const [loadingSeasons, setLoadingSeasons] = useState(() => new Set());
  const [failedSeasons, setFailedSeasons] = useState(() => new Set());
  const [fetchError, setFetchError] = useState(false);
  const [discoverState, setDiscoverState] = useState('idle');
  const pollRefs = useRef(new Map()); // season -> interval id, one entry per in-flight backfill poll
  const navigate = useNavigate();

  const api = useMemo(() => createLeagueApi(leagueId, authFetch, null, apiBaseUrl), [leagueId, authFetch, apiBaseUrl]);

  const refetch = useCallback(async () => {
    setFetchError(false);
    const data = await api.getSeasons();
    setSeasons(data.seasons || []);
  }, [api]);

  useEffect(() => {
    refetch().catch(() => setFetchError(true));
    // Captured up front: by the time this cleanup runs, pollRefs.current
    // could have been reassigned or mutated further (startBackfill adds
    // entries to it), so the cleanup must close over this snapshot of the Map
    // reference rather than re-reading pollRefs.current at cleanup time.
    const pollsAtMount = pollRefs.current;
    return () => {
      pollsAtMount.forEach(id => clearInterval(id));
      pollsAtMount.clear();
    };
  }, [refetch]);

  const startBackfill = useCallback(async (season) => {
    setFailedSeasons(prev => { const next = new Set(prev); next.delete(season); return next; });
    try {
      await api.triggerBackfill(season);
    } catch {
      setFailedSeasons(prev => new Set(prev).add(season));
      return;
    }
    setLoadingSeasons(prev => new Set(prev).add(season));

    // Replace any stale poll already running for this exact season (e.g. a
    // retry) rather than doubling up — different seasons get independent
    // entries in the map, so this never touches another season's poll.
    const existing = pollRefs.current.get(season);
    if (existing) clearInterval(existing);

    let attempts = 0;
    const intervalId = setInterval(async () => {
      attempts++;
      const stopPolling = () => {
        clearInterval(intervalId);
        pollRefs.current.delete(season);
        setLoadingSeasons(prev => { const next = new Set(prev); next.delete(season); return next; });
      };
      try {
        const data = await api.getSeasons();
        setSeasons(data.seasons || []);
        const entry = (data.seasons || []).find(s => s.season === season);
        if (entry && entry.status !== 'discoverable' && entry.ready !== false) {
          stopPolling();
        } else if (attempts >= MAX_POLL_ATTEMPTS) {
          stopPolling();
          setFailedSeasons(prev => new Set(prev).add(season));
        }
      } catch {
        if (attempts >= MAX_POLL_ATTEMPTS) {
          stopPolling();
          setFailedSeasons(prev => new Set(prev).add(season));
        }
      }
    }, POLL_INTERVAL_MS);
    pollRefs.current.set(season, intervalId);
  }, [api]);

  const discoverEarlier = useCallback(async () => {
    setDiscoverState('loading');
    try {
      const knownSeasonNumbers = (seasons || [])
        .map(entry => Number(entry.season))
        .filter(Number.isFinite);
      if (knownSeasonNumbers.length === 0) throw new Error('No seasons available for discovery');
      const targetSeason = Math.min(...knownSeasonNumbers) - 1;
      const data = await api.discoverSeason(targetSeason);
      // Linking the earlier season can merge this league into an older one; open that league.
      if (data.discovered && data.league_id && data.league_id !== leagueId) {
        navigate(`/league/${data.league_id}/home`);
      } else if (data.discovered) {
        await refetch();
        setDiscoverState('idle');
      } else {
        setDiscoverState('exhausted');
      }
    } catch {
      setDiscoverState('error');
    }
  }, [api, refetch, seasons, leagueId, navigate]);

  return { seasons, loadingSeasons, failedSeasons, fetchError, discoverState, refetch, startBackfill, discoverEarlier };
}
